import _ from 'lodash';
import { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  getIntentCategory,
  INTENT_CATEGORIES,
  INTENT_TYPES,
  POSITION_VALUE_TYPES,
  SEGMENT_CONFIG
} from 'v2/common/constants/index';
import { getSelectorBasedStoreValue } from '../../redux/selector';
import {
  isIndexInstrumentPresentInInstrumentGroup, isIndexInstrumentWithWeeklyExpiryOnly,
} from '../../components/InstrumentDetails/indexInstrumentsChecker';
import {
  getDefaultBasketIntent,
  getDefaultIntent,
  getDefaultModifyOrderIntent,
  getDefaultCaseIntent,
  hasCryptoOptionsInstrument
} from '../../configs/index';
import { IntentsContext } from '../../hoc/withIntentsContext';
import ApplicationDomainUtility from 'v3/common/utils/applicationDomain';

const { isCrypto, isAlgoverse, partnerName } = ApplicationDomainUtility;
// const isBasketEnabled = _.get(window, 'userPermissions.combinedPremiumPermission.isEnabled', false);

export const useIntent = (intent) => {
  const {
    onUpdateIntent,
    isPipeIntent,
  } = useContext(IntentsContext);

  const segment = intent?.instrument?.type;
  const intentId = intent?.identifier?.label;
  const instrumentIndex = intent?.instrument?.instrumentIndex;
  const caseIndex = intent?.identifier?.caseIndex;

  const instrumentGroups = useSelector(
    (state) => getSelectorBasedStoreValue(state, 'quantConfig.instrumentGroups')
  );
  const maxInstrumentsInGroup = useSelector(
    (state) => getSelectorBasedStoreValue(state, 'quantConfig.maxInstrumentsInGroup')
  );

  const isIndexInstrumentIntent = useMemo(() => {
    return isIndexInstrumentPresentInInstrumentGroup(instrumentGroups, instrumentIndex);
  }, [instrumentGroups, instrumentIndex]);

  const isIndexInstrumentWithWeeklyExpiry = useMemo(() => {
    return isIndexInstrumentWithWeeklyExpiryOnly(instrumentGroups, instrumentIndex);
  }, [instrumentGroups, instrumentIndex]);

  const createOnChangeHandler = useCallback((field) => (newValue) => {
    const newIntent = _.cloneDeep(intent);
    _.set(newIntent, field, newValue);
    onUpdateIntent(newIntent, intentId);
  }, [intent, intentId, onUpdateIntent]);

  const onChangeSegment = useCallback((newSegment) => {
    let newIntent = _.cloneDeep(intent);
    if (newSegment === INTENT_TYPES.OpenBasket) {
      return onUpdateIntent(
        {
          ...getDefaultBasketIntent(),
          ..._.pick(newIntent, ['identifier']),
          ..._.pick(newIntent, ['isIdle']),
        },
        intentId
      );
    }
    const isPreviousBasket = newIntent?.type === INTENT_TYPES.OpenBasket;
    newIntent = isPreviousBasket ? {
      ...getDefaultIntent(),
      ..._.pick(newIntent, ['identifier']),
      ..._.pick(newIntent, ['isIdle']),
    } : newIntent;

    newIntent.instrument.type = newSegment;

    // when switching into equity from another segment, default quantity type to Amount 
    if (newSegment === SEGMENT_CONFIG.equity) {
      newIntent.quantity = {
        type: POSITION_VALUE_TYPES.Amount,
        value: newIntent.quantity?.value || 1
      }
    }

    if (_.includes([SEGMENT_CONFIG.future, SEGMENT_CONFIG.equity], newSegment)) {
      newIntent.instrument.expiry = {
        index: 1,
        cycle: isCrypto ? 'daily' : 'monthly'
      };
    }

    return onUpdateIntent(newIntent, intentId);
  }, [intent, intentId, onUpdateIntent]);


  const segmentOptionConfigs = useMemo(() => {
    return _.chain(SEGMENT_CONFIG)
      .thru((config) => {
        if (!isCrypto) return config;

        if (partnerName === 'coin_dcx') return [SEGMENT_CONFIG.future]

        const instruments = _.compact(_.flattenDeep(instrumentGroups[instrumentIndex]));
        const shouldOmitEquity =
          _.isEmpty(instruments) || hasCryptoOptionsInstrument(instrumentGroups, instrumentIndex);

        return _.omit(config, shouldOmitEquity ? SEGMENT_CONFIG.equity : [SEGMENT_CONFIG.option, SEGMENT_CONFIG.equity]);
      })
      .thru((config) => (isIndexInstrumentIntent ? _.omit(config, SEGMENT_CONFIG.equity) : config))
      .thru((config) => (
        isPipeIntent ? _.omit(config, [SEGMENT_CONFIG.equity, SEGMENT_CONFIG.future]) : config
      ))
      .map((value, key) => ({ label: value, value: key }))
      .thru((configs) => {
        if (isPipeIntent || isAlgoverse || isCrypto) return configs;
        // if (isPipeIntent || !isBasketEnabled) return configs;

        return [...configs, { label: 'Basket', value: INTENT_TYPES.OpenBasket }];
      })
      .value();
  }, [isIndexInstrumentIntent, isPipeIntent, instrumentGroups, instrumentIndex]);

  const isShowInstrumentIndex = useMemo(() => maxInstrumentsInGroup > 1, [maxInstrumentsInGroup]);

  const onChangeIntentType = useCallback((newIntentType, isIdle = false) => {
    const oldIntentCategory = getIntentCategory(intent?.type);
    const newIntentCategory = getIntentCategory(newIntentType);
    let newIntent;
    let removedOpenIntentIds = [];
    if (oldIntentCategory === newIntentCategory) {
      newIntent = _.cloneDeep(intent);
    } else if (newIntentCategory === INTENT_CATEGORIES.Other) {
      newIntent = getDefaultModifyOrderIntent(caseIndex);
      removedOpenIntentIds = [intentId];
    } else if (newIntentCategory === INTENT_CATEGORIES.Case) {
      newIntent = getDefaultCaseIntent(newIntentType);
    } else {
      newIntent = getDefaultIntent();
    }

    if (newIntentType === INTENT_TYPES.OpenLeg || INTENT_TYPES.OpenBasket) {
      newIntent.isIdle = isIdle;
    }

    const shouldKeepOriginalType =
      intent?.isIdle === true &&
      intent.type === INTENT_TYPES.OpenBasket &&
      newIntentType === INTENT_TYPES.OpenLeg;

    newIntent.type = shouldKeepOriginalType ? intent.type : newIntentType;
    newIntent.identifier = intent.identifier;
    return onUpdateIntent(newIntent, intentId, removedOpenIntentIds);
  }, [intent, intentId, onUpdateIntent, caseIndex]);

  const onUpdateQuickAdjustments = useCallback((newQuickAdjustments) => {
    const newIntent = _.cloneDeep(intent);
    newIntent.uiConfigs = newIntent.uiConfigs || {};
    newIntent.uiConfigs.quickAdjustments = newQuickAdjustments;

    return onUpdateIntent(newIntent, intentId);
  }, [intent, intentId, onUpdateIntent]);

  return {
    segment,
    onChangeSegment,
    isIndexInstrumentIntent,
    isShowInstrumentIndex,
    segmentOptionConfigs,
    intentId,
    onUpdateQuickAdjustments,
    onChangeIntentType,
    createOnChangeHandler,
    maxInstrumentsInGroup,
    isIndexInstrumentWithWeeklyExpiry,
    instrumentGroups,
  };
};
